import { z } from 'zod';
import { TICKET_KINDS, TICKET_STATUSES, type TicketKind, type TicketStatus } from '../constants';
import { NullableText } from './common';

/**
 * Notes: tickets team members send to the administrators — feature ideas, problems, questions. Everyone sends and
 * follows their own; people with `ticket:manage` (administrators) see every ticket, reply and set the status.
 * Messages are text, optionally with attached files (screenshots, documents). Voice input is turned into text in the
 * browser and never uploaded or stored as audio.
 */

export const MAX_TICKET_MESSAGE = 10_000;
/** Files attached to one message. */
export const MAX_TICKET_ATTACHMENTS = 10;
/** Size of one attached file. */
export const MAX_TICKET_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * The only attachment types served inline and shown as thumbnails; every other file is a download, whatever the
 * browser that uploaded it declared (an HTML or SVG file shown inline could run script as the viewer).
 */
export const TICKET_INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

const MessageText = z.string().trim().min(1, 'Write (or say) your message').max(MAX_TICKET_MESSAGE);

/**
 * POST /api/tickets. A blank title is made from the start of the message. Also accepted as multipart/form-data with
 * the text fields `kind`, `title`, `body`, `dictated` ('true' / 'false') plus zero or more file parts named `files`.
 */
export const CreateTicketBody = z.object({
  kind: z.enum(TICKET_KINDS).default('feature'),
  title: NullableText(140).optional(),
  body: MessageText,
  /** Some of the text was dictated (voice typing) — shown as a small hint, since dictation can mishear words. */
  dictated: z.boolean().default(false),
});
export type CreateTicketBody = z.input<typeof CreateTicketBody>;

/**
 * POST /api/tickets/:id/messages — the submitter or an administrator. Also accepted as multipart/form-data: the text
 * fields `body`, `dictated` ('true' / 'false') plus zero or more file parts named `files`.
 */
export const TicketReplyBody = z.object({ body: MessageText, dictated: z.boolean().default(false) });
export type TicketReplyBody = z.input<typeof TicketReplyBody>;

/** PATCH /api/tickets/:id — administrators only; recorded in the ticket's thread. */
export const UpdateTicketBody = z.object({ status: z.enum(TICKET_STATUSES) });
export type UpdateTicketBody = z.infer<typeof UpdateTicketBody>;

/** GET /api/tickets/:id/attachments/:attachmentId — the file, for anyone who can see the ticket. */
export const TicketAttachmentParams = z.object({ id: z.uuid(), attachmentId: z.uuid() });

/** mine: tickets I sent. all: every ticket (administrators; others always get their own). */
export const TICKET_SCOPES = ['mine', 'all'] as const;
export type TicketScope = (typeof TICKET_SCOPES)[number];

/** GET /api/tickets. `scope` defaults to 'all' for administrators, 'mine' for everyone else. */
export const TicketListQuery = z.object({
  scope: z.enum(TICKET_SCOPES).optional(),
  status: z.enum(TICKET_STATUSES).optional(),
});
export type TicketListQuery = z.input<typeof TicketListQuery>;

export type TicketPersonDTO = { id: string; name: string };

export type TicketSummaryDTO = {
  id: string;
  /** Short sequential number shown as #12. */
  number: number;
  kind: TicketKind;
  status: TicketStatus;
  title: string;
  /** Start of the first message. */
  preview: string;
  createdBy: TicketPersonDTO | null;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
  /** Files attached to any message of the ticket. */
  attachmentCount: number;
  /** Something new since the requester last opened it (as the sender, or as an administrator). */
  unread: boolean;
};

export type TicketMessageDTO = {
  id: string;
  author: TicketPersonDTO | null;
  /** Written by an administrator (replies from the admin side are styled differently). */
  fromAdmin: boolean;
  /** Empty for a pure status change. */
  body: string;
  dictated: boolean;
  /** Set when this entry records a status change. */
  newStatus: TicketStatus | null;
  /** Files sent with this message, in the order they were attached (empty for a status change). */
  attachments: TicketAttachmentDTO[];
  createdAt: string;
};

export type TicketAttachmentDTO = {
  id: string;
  /** Name as uploaded (display only; the download URL is by id). */
  filename: string;
  /** As declared by the uploading browser; see TICKET_INLINE_IMAGE_TYPES for what is shown inline. */
  contentType: string;
  /** Bytes. */
  size: number;
};

/** GET /api/tickets/:id — opening a ticket marks it read for the requester. */
export type TicketDTO = TicketSummaryDTO & {
  messages: TicketMessageDTO[];
  /** The requester may set the status (administrator). */
  canManage: boolean;
};

export type TicketListResponse = { items: TicketSummaryDTO[] };

/** GET /api/tickets/unread — sidebar badge: tickets with something new for the requester. */
export type TicketUnreadDTO = { unread: number };
